<script setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import http from "@/http";
import { ElMessage } from "element-plus";
import IconArrowLeft from "@/components/icons/arrows/IconArrowLeft.vue";
import { UploadFilled } from "@element-plus/icons-vue";
import CloseIcon from "@/assets/images/icon-components/CloseIcon.vue";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const baseUrl = `${process.env.VUE_APP_BASE_API_URL}${process.env.VUE_APP_BASE_API_V}`;

const loading = ref(false);
const uploadLoading = ref(false);
const keyList = ref();
const fileInfo = ref();
const form = ref({
    name: null,
    hashId: null,
    key: null,
});
const formRef = ref(null);
const rules = ref({
    name: [
        {
            required: true,
            message: "validations.required_field",
            trigger: "blur",
        },
    ],
    hashId: [
        {
            required: true,
            message: "validations.required_field",
            trigger: "blur",
        },
    ],
    key: [
        {
            required: true,
            message: "validations.required_field",
            trigger: "blur",
        },
    ],
});
const getInstructionKeys = () => {
    loading.value = true;
    http.get(`bank/instruction/keys`)
        .then(({ data }) => {
            keyList.value = data;
        })
        .finally(() => {
            loading.value = false;
        });
};
const getInstruction = (id) => {
    loading.value = true;
    http.get(`bank/instruction/${id}`)
        .then(({ data }) => {
            console.log(data);
            fileInfo.value = data?.file;
            form.value.hashId = data?.file?.hashId;
            form.value.key = data?.key;
            form.value.name = data?.name;
        })
        .finally(() => {
            loading.value = false;
        });
};
const closeFile = () => {
    form.value.hashId = null;
    fileInfo.value = null;
};
const uploadFile = (file) => {
    console.log(file);
    if (file.size <= 20 * 1024 * 1024) {
        uploadLoading.value = true;
        const form_data = new FormData();
        form_data.append("file", file.raw);
        http.post("file/upload", form_data, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
            .then(({ data }) => {
                fileInfo.value = file;
                form.value.hashId = data.hashId;
            })
            .finally(() => {
                uploadLoading.value = false;
            });
    } else {
        ElMessage({
            message: t("notifications.fileSize"),
            type: "warning",
        });
    }
};

const onSubmit = () => {
    formRef.value.validate((valid) => {
        if (valid) {
            loading.value = true;
            let https;

            if (route.params.id) {
                https = http.put(`bank/instruction/${route.params.id}`, form.value);
            } else {
                https = http.post("bank/instruction", form.value);
            }
            https
                .then(() => {
                    ElMessage({
                        message: t("notifications.saved_successfully"),
                        type: "success",
                    });
                    router.go(-1);
                })
                .finally(() => {
                    loading.value = false;
                });
        }
    });
};

onMounted(() => {
    getInstructionKeys();
    if (route.params.id) {
        getInstruction(route.params.id);
    }
});
</script>

<template>
    <div class="p-2">
        <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl">
                {{
                    !route.params.id
                        ? t("menu.InstructionCreateFormPage")
                        : t("menu.InstructionEditFormPage")
                }}
            </h2>
        </div>

        <el-form
            ref="formRef"
            :model="form"
            :rules="rules"
            label-position="top"
            v-loading="loading"
        >
            <div class="grid grid-cols-2 gap-4">
                <el-form-item :label="t('labels.instruction_name')" prop="name">
                    <template #error="{ error }">
                        <span class="el-form-item__error">
                            {{ t(error) }}
                        </span>
                    </template>
                    <el-input
                        size="large"
                        :placeholder="t('labels.instruction_name')"
                        v-model="form.name"
                        type="text"
                    />
                </el-form-item>

                <el-form-item :label="t('placeholders.select.selectKey')" prop="key">
                    <template #error="{ error }">
                        <span class="el-form-item__error">
                            {{ t(error) }}
                        </span>
                    </template>
                    <el-select
                        style="width: 100%; margin-right: 10px"
                        v-model="form.key"
                        size="large"
                        :placeholder="t('placeholders.select.selectKey')"
                    >
                        <el-option
                            v-for="(instruction, index) in keyList"
                            :key="index"
                            :value="instruction"
                            :label="instruction"
                        />
                    </el-select>
                </el-form-item>
                <el-form-item :label="t('labels.enter_key')" prop="hashId">
                    <template #error="{ error }">
                        <span class="el-form-item__error">
                            {{ t(error) }}
                        </span>
                    </template>
                    <template v-if="form.hashId">
                        <div class="flex items-center">
                            <a
                                class="underline mr-2"
                                :href="`${baseUrl}/file/download?hashId=${form?.hashId}`"
                                download
                                target="_blank"
                                rel="opener"
                            >
                                {{ fileInfo?.name }}
                            </a>
                            <el-button size="small" @click="closeFile">
                                <template #icon>
                                    <close-icon />
                                </template>
                            </el-button>
                        </div>
                    </template>
                    <template v-else>
                        <el-upload
                            action="#"
                            :auto-upload="false"
                            :show-file-list="false"
                            :multiple="false"
                            :size="1024"
                            :on-change="uploadFile"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png"
                        >
                            <el-button
                                :loading="uploadLoading"
                                class="upload-btn"
                                type="info"
                                plain
                                size="large"
                            >
                                <el-icon>
                                    <UploadFilled />
                                </el-icon>
                                <span>{{ t("placeholders.input.fileUpload") }}</span>
                            </el-button>
                        </el-upload>
                    </template>
                </el-form-item>
            </div>

            <div></div>

            <div class="flex items-center justify-end">
                <el-space>
                    <el-button @click="() => router.go(-1)">
                        <el-icon>
                            <IconArrowLeft />
                        </el-icon>
                        <span> {{ t("buttons.back") }}</span>
                    </el-button>
                    <el-button type="primary" @click="onSubmit">
                        {{ t("buttons.save") }}
                    </el-button>
                </el-space>
            </div>
        </el-form>
    </div>
</template>
